/**
 * Kleine Vektor- und Vierecksgeometrie für den Scan-Zuschnitt.
 *
 * Alle Vierecke sind im Uhrzeigersinn geordnet – oben-links, oben-rechts,
 * unten-rechts, unten-links – und zwar in *Bildschirmkoordinaten*, y zeigt also
 * nach unten. Ob die Koordinaten normalisiert (0…1) oder Pixel sind, hängt vom
 * Aufrufer ab; die Funktionen hier rechnen mit beidem.
 */

export interface Point {
  x: number;
  y: number;
}

export type Quad = [Point, Point, Point, Point];

export function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Fläche nach Gauß'scher Trapezformel, vorzeichenlos. */
export function quadArea(quad: Quad): number {
  let sum = 0;
  for (let i = 0; i < 4; i++) {
    const a = quad[i]!;
    const b = quad[(i + 1) % 4]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/** Konvex heißt: alle vier Kreuzprodukte haben dasselbe Vorzeichen. */
export function isConvex(quad: Quad): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = quad[i]!;
    const b = quad[(i + 1) % 4]!;
    const c = quad[(i + 2) % 4]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross === 0) return false;
    const s = Math.sign(cross);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/**
 * Wie nahe die vier Innenwinkel an 90° liegen – 1 bei einem perfekten
 * Rechteck, 0 bei 30° oder 150°. Zurückgegeben wird der *schlechteste* Winkel,
 * denn ein einzelner ausgerissener Eckpunkt verdirbt den Zuschnitt komplett.
 */
export function squareness(quad: Quad): number {
  let worst = 1;
  for (let i = 0; i < 4; i++) {
    const prev = quad[(i + 3) % 4]!;
    const cur = quad[i]!;
    const next = quad[(i + 1) % 4]!;
    const a1 = Math.atan2(prev.y - cur.y, prev.x - cur.x);
    const a2 = Math.atan2(next.y - cur.y, next.x - cur.x);
    let deg = Math.abs(((a1 - a2) * 180) / Math.PI) % 360;
    if (deg > 180) deg = 360 - deg;
    const score = 1 - Math.abs(deg - 90) / 60;
    if (score < worst) worst = score;
  }
  return Math.max(0, worst);
}

/**
 * Vier beliebig gefundene Schnittpunkte in die Reihenfolge
 * TL → TR → BR → BL bringen.
 *
 * Erst im Uhrzeigersinn um den Schwerpunkt sortieren, dann so rotieren, dass
 * die Ecke mit der kleinsten Summe x+y vorne steht. Das ist bei einem schräg
 * abfotografierten Foto die Ecke, die der oberen linken Bildecke am nächsten
 * liegt – also die, die der Betrachter „oben links" nennen würde.
 */
export function orderQuad(points: Point[]): Quad | null {
  if (points.length !== 4) return null;

  const cx = points.reduce((s, p) => s + p.x, 0) / 4;
  const cy = points.reduce((s, p) => s + p.y, 0) / 4;

  // atan2 wächst in Bildschirmkoordinaten (y nach unten) im Uhrzeigersinn.
  const sorted = [...points].sort(
    (a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx),
  );

  let start = 0;
  let best = Infinity;
  for (let i = 0; i < 4; i++) {
    const sum = sorted[i]!.x + sorted[i]!.y;
    if (sum < best) {
      best = sum;
      start = i;
    }
  }

  return [
    sorted[start]!,
    sorted[(start + 1) % 4]!,
    sorted[(start + 2) % 4]!,
    sorted[(start + 3) % 4]!,
  ];
}

/**
 * Dreht die *Ausgabe* um 90° pro Schritt, indem die Ecken durchgeschoben
 * werden. Wird die Ecke „oben links" auf die vormalige „oben rechts" gesetzt,
 * entsteht beim Entzerren ein um eine Vierteldrehung gekipptes Bild – ohne
 * dass ein zweiter Rechendurchgang nötig wäre.
 */
export function rotateQuad(quad: Quad, steps: number): Quad {
  const k = ((steps % 4) + 4) % 4;
  return [quad[k % 4]!, quad[(k + 1) % 4]!, quad[(k + 2) % 4]!, quad[(k + 3) % 4]!];
}

/**
 * Das Viereck ein Haar nach innen ziehen.
 *
 * Die erkannte Kante liegt *auf* dem Übergang zwischen Foto und Untergrund.
 * Nimmt man sie wörtlich, bleibt im Ergebnis rundherum ein dünner Streifen
 * Tischplatte stehen – auffällig genau dann, wenn der Untergrund dunkel ist.
 * Ein Zehntelprozent nach innen kostet zwei Pixel Bildinhalt und spart den
 * Rand.
 */
export function shrinkQuad(quad: Quad, fraction: number): Quad {
  const cx = (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4;
  const cy = (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4;
  const factor = 1 - fraction;
  return quad.map((p) => ({
    x: cx + (p.x - cx) * factor,
    y: cy + (p.y - cy) * factor,
  })) as Quad;
}

/** Voreinstellung, wenn nichts erkannt wurde: ein Rahmen mit etwas Luft. */
export function insetQuad(inset = 0.06): Quad {
  const a = inset;
  const b = 1 - inset;
  return [
    { x: a, y: a },
    { x: b, y: a },
    { x: b, y: b },
    { x: a, y: b },
  ];
}

export const FULL_QUAD: Quad = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

export function clampQuad(quad: Quad): Quad {
  return quad.map((p) => ({ x: clamp(p.x, 0, 1), y: clamp(p.y, 0, 1) })) as Quad;
}
